import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import classNames from 'classnames'

import Modal from '@/components/Modal'
import { CHAT_FAQ_SCOPES } from '@/constant/chat-faq'

const validationSchema = yup.object().shape({
  question: yup.string().trim().max(500).required(),
  answer: yup.string().trim().max(4000).required(),
  scope: yup.string().oneOf(CHAT_FAQ_SCOPES.map(option => option.value)).required(),
  recommendedForChat: yup.boolean()
})

const ChatFaqFormModal = ({ faq, isSaving, onClose, onSubmit, recommendedCount = 0 }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      question: '',
      answer: '',
      scope: 'GENERAL',
      recommendedForChat: false
    }
  })
  const isAlreadyRecommended = faq?.recommendedForChat === true
  const recommendationLimitReached = !isAlreadyRecommended && recommendedCount >= 4
  const isRecommendedForChat = watch('recommendedForChat')

  useEffect(() => {
    reset({
      question: faq?.question || '',
      answer: faq?.answer || '',
      scope: faq?.scope || 'GENERAL',
      recommendedForChat: faq?.recommendedForChat === true
    })
  }, [faq, reset])

  return (
    <Modal visibleState={[true, onClose]} width="w-1/2">
      <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
        <div className="text-xl font-bold mb-5">
          {faq ? 'Edit FAQ' : 'Create FAQ'}
        </div>

        <label className="font-semibold mb-1" htmlFor="chat-faq-scope">
          Scope
        </label>
        <select
          id="chat-faq-scope"
          className={classNames({
            'p-3 border border-gray-300 rounded-lg mb-4': true,
            'border-red-500': !!errors.scope
          })}
          {...register('scope')}
        >
          {CHAT_FAQ_SCOPES.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <label className="font-semibold mb-1" htmlFor="chat-faq-question">
          Question
        </label>
        <textarea
          id="chat-faq-question"
          rows="3"
          className={classNames({
            'p-3 border border-gray-300 rounded-lg': true,
            'border-red-500': !!errors.question
          })}
          {...register('question')}
        />
        <div className="flex justify-between mb-4">
          <small className="text-red-500">
            {errors.question?.message}
          </small>
          <small className="text-gray-500">Maximum 500 characters</small>
        </div>

        <label className="font-semibold mb-1" htmlFor="chat-faq-answer">
          Approved answer
        </label>
        <textarea
          id="chat-faq-answer"
          rows="8"
          className={classNames({
            'p-3 border border-gray-300 rounded-lg': true,
            'border-red-500': !!errors.answer
          })}
          {...register('answer')}
        />
        <div className="flex justify-between">
          <small className="text-red-500">
            {errors.answer?.message}
          </small>
          <small className="text-gray-500">Maximum 4,000 characters</small>
        </div>

        <label className="mt-4 flex items-start gap-x-3 text-sm text-gray-700" htmlFor="chat-faq-recommended">
          <input
            id="chat-faq-recommended"
            type="checkbox"
            className="mt-0.5"
            {...register('recommendedForChat')}
          />
          <span>
            <span className="block font-semibold">Recommend in storefront chat</span>
            <span className="block text-xs text-gray-500">Published recommendations appear as selectable questions for customers starting a new bot chat.</span>
          </span>
        </label>
        {
          recommendationLimitReached && isRecommendedForChat && !isAlreadyRecommended && (
            <div className="mt-2 text-xs text-amber-700">
              This will replace the oldest storefront suggestion. A maximum of four published suggestions can appear at once.
            </div>
          )
        }

        {
          faq?.status === 'PUBLISHED' && (
            <div className="mt-4 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
              Editing the question or answer regenerates this published FAQ&apos;s Gemini embedding.
            </div>
          )
        }

        <div className="flex justify-end gap-x-3 mt-6">
          <button
            type="button"
            className="px-6 py-2 rounded-full border border-gray-800"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded-full border border-gray-800 bg-gray-800 text-white disabled:opacity-50"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : faq ? 'Save changes' : 'Save draft'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default ChatFaqFormModal
